import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import KibTag from '@chewy/kib-tag-react'
import { type Constant, DateUtils, Utils } from '@pbt/pbt-ui-components'

import type { DashboardLabTestEntry } from '~/api/graphql/generated/types'
import LabActionsCell from '~/components/dashboard/soapV2/summary/labs/LabActionsCell'
import LabNameCell from '~/components/dashboard/soapV2/summary/labs/LabNameCell'
import {
  ColorVariantToTheme,
  LabStatusToColorVariant,
} from '~/constants/colors'
import {
  LabTestsStateTab,
  type LabTestsStateTabType,
  useLabTestsSubscription,
} from '~/hooks/useLabTestsSubscription'
import i18n from '~/locales/i18n'
import { getLabTestsStates } from '~/store/reducers/constants'

import PatientHistoryEmptyState from '../PatientHistoryEmptyState'
import PatientHistoryWidget, { type TabData } from '../PatientHistoryWidget'

import '@chewy/kib-tag-styles/dist/kib-tag.css'
import styles from './LabsHistoryWidget.module.scss'

/** Replaces hyphens with non-breaking hyphen (U+2011) so values like "In-house" do not break and truncate as "In-...". */
export const formatLabTypeForDisplay = (
  labType: string | null | undefined,
): string => (labType ? labType.replace(/-/g, '\u2011') : '--')

const labsColumns = (
  clientId: string,
  patientId: string,
  LabTestsStatesList: Constant[],
) => [
  {
    id: 'date',
    prop: (item: DashboardLabTestEntry) =>
      DateUtils.formatDate(item.orderDate, false, 'MM/DD/YY'),
    label: i18n.t('Common:DATE_TIME'),
  },
  {
    id: 'labName',
    component: (item: DashboardLabTestEntry) => <LabNameCell item={item} />,
    label: i18n.t('Common:LAB'),
  },
  {
    id: 'type',
    prop: (item: DashboardLabTestEntry) =>
      formatLabTypeForDisplay(item.testLogs?.[0]?.labType),
    label: i18n.t('Common:TYPE_ONE'),
  },
  {
    id: 'status',
    component: (item: DashboardLabTestEntry) => (
      <div className={styles.statusCell}>
        {item.testLogs?.map((test) => (
          <div className={styles.statusTag} key={test.id}>
            <KibTag
              theme={
                ColorVariantToTheme[
                  LabStatusToColorVariant[
                    Utils.getConstantName(
                      test.statusId || '',
                      LabTestsStatesList,
                    )
                  ]
                ]
              }
            >
              <span className={styles.statusText}>
                {Utils.getConstantName(test.statusId || '', LabTestsStatesList)}
              </span>
            </KibTag>
          </div>
        ))}
      </div>
    ),
    label: i18n.t('Common:STATUS'),
  },
  {
    id: 'actions',
    component: (item: DashboardLabTestEntry) => (
      <LabActionsCell clientId={clientId} item={item} patientId={patientId} />
    ),
    label: '',
    isAction: true,
  },
]

interface LabsHistoryWidgetProps {
  clientId: string
  patientId: string
}

const LIMIT = 5

const LabsHistoryWidget = ({ clientId, patientId }: LabsHistoryWidgetProps) => {
  const { t } = useTranslation(['Clients'])
  const [selectedTab, setSelectedTab] = useState<LabTestsStateTabType>(
    LabTestsStateTab.COMPLETED_TAB_ID,
  )

  const [currentPage, setCurrentPage] = useState(1)

  const offset = (currentPage - 1) * LIMIT

  const {
    completedLabTests,
    inProgressLabTests,
    resultsReceivedLabTests,
    loading: isLoading,
    completedLabTestsCount,
    inProgressLabTestsCount,
    resultsReceivedLabTestsCount,
  } = useLabTestsSubscription(patientId, offset, LIMIT, selectedTab)

  const LabTestsStatesList = useSelector(getLabTestsStates)

  const chronologicallySortLabTests = (
    a: DashboardLabTestEntry,
    b: DashboardLabTestEntry,
  ) => {
    const dateA = new Date(a.orderDate)
    const dateB = new Date(b.orderDate)
    return dateB.getTime() - dateA.getTime()
  }

  const completedLabs = useMemo(
    () => [...completedLabTests].sort(chronologicallySortLabTests),
    [completedLabTests],
  )

  const inProgressLabs = useMemo(
    () => [...inProgressLabTests].sort(chronologicallySortLabTests),
    [inProgressLabTests],
  )

  const resultsReceivedLabs = useMemo(
    () => [...resultsReceivedLabTests].sort(chronologicallySortLabTests),
    [resultsReceivedLabTests],
  )

  const columns = useMemo(
    () => labsColumns(clientId, patientId, LabTestsStatesList),
    [clientId, patientId, LabTestsStatesList],
  )

  const tabs: TabData<DashboardLabTestEntry>[] = [
    {
      id: LabTestsStateTab.COMPLETED_TAB_ID,
      label: t('Clients:LABS_WIDGET.COMPLETED'),
      labelCount: completedLabTestsCount,
      data: completedLabs,
      columns,
      totalCount: completedLabTestsCount,
      noRecords: (
        <PatientHistoryEmptyState
          message={t('Clients:LABS_WIDGET.NO_COMPLETED_LABS')}
        />
      ),
    },
    {
      id: LabTestsStateTab.IN_PROGRESS_TAB_ID,
      label: t('Clients:LABS_WIDGET.IN_PROGRESS'),
      labelCount: inProgressLabTestsCount,
      data: inProgressLabs,
      columns,
      totalCount: inProgressLabTestsCount,
      noRecords: (
        <PatientHistoryEmptyState
          message={t('Clients:LABS_WIDGET.NO_IN_PROGRESS_LABS')}
        />
      ),
    },
    {
      id: LabTestsStateTab.RESULTS_RECEIVED_TAB_ID,
      label: t('Clients:LABS_WIDGET.RESULTS_RECEIVED'),
      labelCount: resultsReceivedLabTestsCount,
      data: resultsReceivedLabs,
      columns,
      totalCount: resultsReceivedLabTestsCount,
      noRecords: (
        <PatientHistoryEmptyState
          message={t('Clients:LABS_WIDGET.NO_RESULTS_RECEIVED_LABS')}
        />
      ),
    },
  ]

  const handleTabChange = (tabId: number) => {
    setCurrentPage(1)
    setSelectedTab(tabId as LabTestsStateTabType)
  }

  return (
    <PatientHistoryWidget
      className={styles.labsWidget}
      currentPage={currentPage}
      isLoading={isLoading}
      layoutOptions={{
        flushRows: true,
      }}
      maxRows={LIMIT}
      selectedTab={selectedTab}
      tabs={tabs}
      title={t('Clients:LABS_WIDGET.TITLE')}
      onPageChange={setCurrentPage}
      onSelectedTabChange={handleTabChange}
    />
  )
}

export default LabsHistoryWidget
